import { api_fetch } from "@/api/client.tsx"
import { Split } from "@/api/accounting.tsx"
import { DeductionKind } from "@/api/deduction"
import { toast } from "@/components/ui/use-toast.ts"
import { toastResult } from "@/toast.tsx"
import { coding_amount, coding_pill_notice } from "@/utils/transaction_amount"
import { start_bulk_coding_wiz } from "./bulk_coding_wiz_state"
import {
  coding_wiz_run_mode,
  coding_wiz_split_grouping_valid,
  resolve_coding_wiz_feedback,
  type CodingWizPreferences,
} from "./coding_wiz_preferences"

/**
 * Bulk coding actions shared by every list that codes a selection of transactions — the deduction
 * list's selection bar and the Open Deductions queue — so both book the same amounts through the
 * same endpoints.
 */

/** What a bulk coding action needs to know about each selected transaction. */
export interface BulkCodingRow {
  id: string
  /** The payer, so a UNFI PP row is left out of a bulk code (`coding_pill_notice`). */
  source?: string
  kind: DeductionKind
  invoice_amount: number
  discount_amount?: number | null
  invoice_number: string
}

export interface BulkFullCodeInput {
  transaction_id: string
  splits: Partial<Split>[]
}

/**
 * PURE: the `/split-deductions` body that fully codes each row to one line built from the dialog's
 * values, at the row's own coding amount (early-pay invoices book only their discount).
 */
export function bulk_full_code_body(rows: BulkCodingRow[], form_values: Partial<Split>): BulkFullCodeInput[] {
  const split_data: Record<string, unknown> = { ...form_values }
  delete split_data["customer_name"]
  delete split_data["name"] // account name is unneeded, we get account from account_id
  delete split_data["id"] // no id for new splits
  delete split_data["amount"] // amount is always set per transaction
  for (const key of Object.keys(split_data)) {
    if (split_data[key] === "") {
      delete split_data[key]
    }
  }
  // An invoice with no early-pay discount has nothing to code; a $0.00 line on it is noise. A UNFI
  // PP row's cash cannot be coded at all, and `/split-deductions` stops at the first refused row,
  // so leaving one in would code the rows before it and silently skip the rows after.
  return rows
    .filter((row: BulkCodingRow) => coding_pill_notice(row) === null)
    .map((row: BulkCodingRow) => ({
      transaction_id: row.id,
      splits: [{ ...(split_data as Partial<Split>), amount: coding_amount(row.kind, row.invoice_amount, row.discount_amount) }],
    }))
}

/** Fully code every row to one line. Returns whether the save succeeded (a toast reports it either way). */
export async function bulk_full_code(rows: BulkCodingRow[], form_values: Partial<Split>): Promise<boolean> {
  const res = await api_fetch("/split-deductions", { method: "POST", body: bulk_full_code_body(rows, form_values) })
  toastResult(res, `${rows.length} transaction${rows.length > 1 ? "s" : ""} fully coded`, "Failed to fully code transactions")
  return res.ok
}

/** Code every row to one ledger account (reason code) for its full amount. */
export async function bulk_code_to_account(deduction_ids: string[], reason_code_id: string): Promise<boolean> {
  const body = deduction_ids.map((deduction_id: string) => ({ deduction_id, reason_code_id }))
  const res = await api_fetch("/match-deductions", { body })
  toastResult(
    res,
    `${deduction_ids.length} accounting match${deduction_ids.length > 1 ? "es" : ""} completed`,
    "Failed to assign accounting match"
  )
  return res.ok
}

/**
 * Start a background CodingWiz run over the rows with the team's saved preferences
 * (`use_coding_wiz_preferences`). Returns false (with a toast) when the preferences are unusable
 * or a run is already going.
 */
export function start_bulk_coding_wiz_for_rows(rows: BulkCodingRow[], prefs: CodingWizPreferences, is_accounting: boolean): boolean {
  if (!coding_wiz_split_grouping_valid(prefs)) {
    toast({
      title: "CodingWiz defaults need attention",
      description:
        "Split-into-lines mode requires at least one grouping option. Fix it under Settings › Accounting › CodingWiz defaults.",
      variant: "destructive",
    })
    return false
  }
  const labels: Record<string, string> = {}
  for (const row of rows) {
    labels[row.id] = row.invoice_number
  }
  return start_bulk_coding_wiz({
    deduction_ids: rows.map((row: BulkCodingRow) => row.id),
    mode: coding_wiz_run_mode(prefs),
    feedback: resolve_coding_wiz_feedback(prefs),
    is_accounting,
    labels,
  })
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("bulk_full_code_body", () => {
    const deduction: BulkCodingRow = { id: "d1", kind: "deduction", invoice_amount: -0.39, invoice_number: "91826" }
    const early_pay: BulkCodingRow = { id: "i1", kind: "invoice", invoice_amount: 980, discount_amount: -20, invoice_number: "INV1" }

    it("skips an invoice paid with no early-pay discount", () => {
      const no_discount: BulkCodingRow = { id: "i2", kind: "invoice", invoice_amount: 2786.62, discount_amount: 0, invoice_number: "24531" }
      const body = bulk_full_code_body([no_discount, deduction], { account_id: "acct" } as Partial<Split>)
      expect(body).toEqual([{ transaction_id: "d1", splits: [{ account_id: "acct", amount: 0.39 }] }])
    })

    it("leaves a UNFI PP reversal out, so the rows after it still get coded", () => {
      const reversal: BulkCodingRow = { id: "pp", source: "unfi", kind: "prepayment", invoice_amount: -5214.74, invoice_number: "SI00004145PP" }
      const clawback: BulkCodingRow = { id: "cb", source: "unfi", kind: "deduction", invoice_amount: -5214.74, invoice_number: "SI00004145" }
      const body = bulk_full_code_body([reversal, clawback], { account_id: "acct" } as Partial<Split>)
      expect(body).toEqual([{ transaction_id: "cb", splits: [{ account_id: "acct", amount: 5214.74 }] }])
    })

    it("codes each row at its own amount and drops form-only fields", () => {
      const body = bulk_full_code_body([deduction, early_pay], {
        account_id: "acct",
        name: "4603 Other Deductions",
        id: "old",
        amount: 999,
        customer_name: "Walmart",
        code_description: "",
      } as Partial<Split>)
      expect(body).toEqual([
        { transaction_id: "d1", splits: [{ account_id: "acct", amount: 0.39 }] },
        { transaction_id: "i1", splits: [{ account_id: "acct", amount: 20 }] },
      ])
    })
  })
}
